import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';
import { Button, Flex, Layout, Menu, Spin } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { MenuProps } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useLocalStorageState } from 'ahooks';
import { SidebarLogo } from './SidebarLogo';
import { getMenuItems, MenuConfiguration } from './menu-config';
import { BETA_TOGGLES } from '../../../features/beta/screens/Beta';
import { SidebarActionCards } from './sidebar-action-cards/index';

import { useHasMultipleLocations } from '../../../shared/hooks/use-has-multiple-locations';
import { useAppSelector } from '../../../shared/hooks/use-app-selector';
import { useAppDispatch } from '../../../shared/hooks/use-app-dispatch';

import { t } from '../../../utils/i18n';
import { changeLocation } from '../../../actions';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import createQueryConfig from '../../../queries';
import {
  isResellerAccount,
  isWhitelabelAccount,
  isTrialPhasePromoActive,
  isUpsellShorePOSActive,
  isShorePOSExternalLinkActive,
  isAdmin,
} from '../../../shared/utils';
import useMerchantSettings from '../../../hooks/use-merchant-settings';
import { getAppSettings } from '../../../selectors';
import { BOOKING_BASE_URL, POS_MANAGER_BASE_URL } from '../../../constants';
import EVENT_BUS_EVENTS from '../../../constants/event-bus-events';
import { buildScreenRouteMapping } from '../../../constants/screen-routes';
import { touchTargetStyles } from '../touch-target';

const { Sider } = Layout;

const StyledSider = styled(Sider)<{ $fullWidth?: boolean }>`
  background: white;
  border-right: none;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 100vh;
  border-inline-end: 1px solid ${({ theme }) => theme.antd.colorBorderSecondary};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 3px;
  }

  .ant-layout-sider-trigger {
    display: none;
  }

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      /* Antd writes width/flex-basis onto the Sider as inline styles, so the
         full-bleed override (mobile drawer) has to outrank them. */
      flex: 1 1 auto !important;
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      /* The drawer already owns the viewport height, and its own header eats a
         few rows of it — 100vh here would add a redundant scrollbar. */
      min-height: 100%;
      border-inline-end: none;
      /* Installed as a standalone PWA the sheet runs under the home indicator.
         The notch is the sticky header's business, since that is what sits
         against the top edge once this container scrolls. */
      padding-bottom: env(safe-area-inset-bottom, 0px);
    `}
`;

const StyledMenu = styled(Menu)`
  padding: 5px;
  background: transparent;
  border-inline-end: unset !important;

  .anticon {
    color: rgba(0, 0, 0, 0.45) !important;
  }

  .ant-v5-menu-item {
    height: 48px;
  }

  .menu-item-locked {
    color: ${({ theme }) => theme.antd.colorTextDisabled};

    .anticon {
      color: rgba(0, 0, 0, 0.25) !important;
    }
  }

  .ant-v5-menu-submenu {
    border-radius: unset;
    border-top: 1px solid ${({ theme }) => theme.antd.colorBorderSecondary};

    .ant-v5-menu-submenu-title {
      text-transform: uppercase;
    }
  }
`;

// In drawer mode the logo shares a row with the close button instead of sitting
// on a centred row of its own, which saved a header's worth of vertical space on
// the short viewports the drawer is built for. The close button leads the row so
// it lands where the header's menu button just was, rather than making a thumb
// cross the phone to dismiss a full-screen sheet.
const StyledDrawerHeader = styled(Flex)`
  /* The close button brings its own padding, so a small start inset lands its
     glyph on the same rail as the menu icons: StyledMenu's own 5px plus the
     padding token, which is what antd derives itemPaddingInline from. */
  padding-inline: 5px calc(5px + ${({ theme }) => theme.antd.padding}px);
  gap: 4px;

  /* The sider scrolls once the menu outgrows the viewport (expanded sections, or
     a short screen) and this row is its first child. Letting it scroll away
     strands the sheet: a full-bleed drawer exposes no backdrop to tap, so the
     close button is the only exit that does not navigate somewhere. */
  position: sticky;
  top: 0;
  z-index: 1;
  background: white;
  /* Standalone PWA: this row is what meets the top edge, notch included. */
  padding-top: env(safe-area-inset-top, 0px);
`;

const StyledDrawerCloseButton = styled(Button)`
  flex-shrink: 0;
  ${touchTargetStyles}
`;

const SIDE_MENU_CLICK_EVENT = 'side_menu_item_clicked';

interface SideMenuProps {
  screenName: string;
  sideMenuCollapsed: boolean;
  /**
   * Render the menu full-bleed instead of as the fixed-width desktop rail.
   * Used by the mobile drawer, where the menu owns the whole viewport.
   */
  fullWidth?: boolean;
  /**
   * Called after a menu item navigates, so the mobile drawer can close itself.
   */
  onNavigate?: () => void;
  /**
   * When given, the menu renders its own close button next to the logo. The
   * mobile drawer passes this and drops antd's header row, so the two do not
   * stack up.
   */
  onClose?: () => void;
}

export function SideMenu({
  sideMenuCollapsed,
  screenName,
  fullWidth = false,
  onNavigate,
  onClose,
}: SideMenuProps) {
  const dispatch = useAppDispatch();
  const { queryClient, apiClient, trackMethod, eventBus } = useGlobals();
  const queryConfig = createQueryConfig(queryClient, apiClient);
  const { merchantSettings } = useMerchantSettings();
  const currentMerchant = useAppSelector((state) => state.currentMerchant);
  const currentAccount = useAppSelector((state) => state.currentAccount);
  const sessionPermissions = useAppSelector(
    (state) => state.sessionPermissions
  );
  const { subscriptionPartner } = useAppSelector(getAppSettings);

  const accountLocale = currentAccount?.locale || 'en';
  const { company, trialDaysLeft = 0 } = merchantSettings || {};

  const { data: merchantData } = useQuery<{ slug?: string }>({
    ...queryConfig.merchantV2(currentMerchant.id ?? ''),
    enabled: Boolean(currentMerchant.id),
  });

  const hasMultipleLocations = useHasMultipleLocations();
  const { mutate: mutateGeneratePOSLink } = useMutation({
    ...queryConfig.ssoLink.generatePOSLink({}),
    onSuccess: ({ token }) => {
      if (token) {
        const redirectPOSUrl = `${POS_MANAGER_BASE_URL}/instant-login?token=${token}`;
        window.open(redirectPOSUrl, '_blank');
      } else {
        const redirectPOSUrl = `${POS_MANAGER_BASE_URL}/login`;
        window.open(redirectPOSUrl, '_blank');
      }
    },
    onError: () => {
      const redirectPOSUrl = `${POS_MANAGER_BASE_URL}/login`;
      window.open(redirectPOSUrl, '_blank');
    },
  });

  const [showCalendarV2] = useLocalStorageState<boolean>(
    BETA_TOGGLES.calendarV2.key,
    {
      defaultValue: false,
      listenStorageChange: true,
    }
  );

  const [showGravityBasedApps] = useLocalStorageState<boolean>(
    BETA_TOGGLES.gravityBasedApps.key,
    {
      defaultValue: false,
      listenStorageChange: true,
    }
  );

  const [selectedKey, setSelectedKey] = useState<string>('');
  const [openKeys, setOpenKeys] = useLocalStorageState<string[]>(
    'as:menuOpenKeys',
    {
      defaultValue: [],
      listenStorageChange: false,
    }
  );

  const isInitialized = useRef(false);
  const prevScreenName = useRef('');
  const prevSideMenuCollapsed = useRef(sideMenuCollapsed);

  const menuConfig: MenuConfiguration = useMemo(() => {
    const isTrialUser = isTrialPhasePromoActive({
      companyName: company ?? 'shore',
      trialDaysLeft,
      roles: (currentAccount?.roles || []).map((r) => r.name),
    });

    const bookingPageUrl = `${BOOKING_BASE_URL}/widget/${merchantData?.slug}?locale=${accountLocale}`;

    const isPOSUpsellActive = isUpsellShorePOSActive({
      locale: accountLocale,
      companyName: company ?? 'shore',
      packageCodes: currentAccount?.contractDetails?.packages ?? [],
    });

    const isPOSExternalLinkActive = isShorePOSExternalLinkActive({
      companyName: company ?? 'shore',
      packageCodes: currentAccount?.contractDetails?.packages ?? [],
    });

    const isWhitelabel = isWhitelabelAccount(currentMerchant.whiteLabel);
    const isReseller = isResellerAccount(subscriptionPartner);
    const isUserAdmin = isAdmin({ currentAccount, currentMerchant });

    return {
      sessionPermissions,
      isTrialUser,
      isWhitelabel,
      isReseller,
      isUserAdmin,
      isPOSUpsellActive,
      isPOSExternalLinkActive,
      bookingPageUrl,
      posManagerUrl: POS_MANAGER_BASE_URL,
      merchantSlug: merchantData?.slug,
      currentAccountId: currentAccount?.id ?? undefined,
      hasMultipleLocations,
      source: currentMerchant?.source,
      showCalendarV2,
      showGravityBasedApps,
      showAIReceptionist: Boolean(
        currentMerchant?.featureToggles?.ai_receptionist
      ),
      translate: t,
    };
  }, [
    company,
    trialDaysLeft,
    currentAccount,
    hasMultipleLocations,
    merchantData,
    accountLocale,
    currentMerchant,
    subscriptionPartner,
    sessionPermissions,
    showCalendarV2,
    showGravityBasedApps,
  ]);

  const menuItems = useMemo(
    () => getMenuItems(menuConfig),
    [menuConfig]
  ) as MenuProps['items'];

  const isDataLoading = useMemo(() => {
    return (
      Object.values(sessionPermissions).every((value) => value == null) ||
      !currentAccount?.id
    );
  }, [sessionPermissions, currentAccount]);

  const screenRouteMapping = useMemo(
    () =>
      buildScreenRouteMapping({
        bookingPageUrl: menuConfig.bookingPageUrl,
        posManagerUrl: POS_MANAGER_BASE_URL,
      }),
    [menuConfig.bookingPageUrl]
  );

  useEffect(() => {
    const mapping = screenRouteMapping[screenName];

    if (!mapping) {
      if (screenName.includes('#/')) {
        if (prevScreenName.current !== screenName || !isInitialized.current) {
          prevScreenName.current = screenName;
          if (selectedKey !== screenName) {
            setSelectedKey(screenName);
          }
          isInitialized.current = true;
        }
      }
      return;
    }

    if (prevScreenName.current === screenName && isInitialized.current) {
      return;
    }
    prevScreenName.current = screenName;
    const newSelectedKey = mapping.key;

    if (selectedKey !== newSelectedKey) {
      setSelectedKey(newSelectedKey);
    }

    // Update openKeys based on parent, but only if not collapsed
    // Don't re-trigger if only collapsed changed
    if (!sideMenuCollapsed && mapping.parent) {
      const newOpenKeys = [mapping.parent];
      if (JSON.stringify(openKeys) !== JSON.stringify(newOpenKeys)) {
        setOpenKeys(newOpenKeys);
      }
    } else if (sideMenuCollapsed && openKeys && openKeys.length > 0) {
      // Clear openKeys when collapsed
      setOpenKeys([]);
    }

    isInitialized.current = true;
    prevSideMenuCollapsed.current = sideMenuCollapsed;
  }, [screenRouteMapping, screenName, sideMenuCollapsed]);

  const handleOpenChange = (keys: string[]) => {
    if (!keys || keys.length === 0) {
      setOpenKeys([]);
      return;
    }
    const latest = keys[keys.length - 1];
    setOpenKeys(latest ? [latest] : []);
  };

  // TODO: Make this onClick handler part better in next iteration
  const handleOnMenuClick: MenuProps['onClick'] = (e) => {
    const menuItem = menuItems?.find((item) => item?.key === e.key);
    if (menuItem && 'disabled' in menuItem && menuItem.disabled) {
      return;
    }

    // Every branch below either navigates or opens a new tab, so the mobile
    // drawer can be dismissed once for all of them.
    onNavigate?.();

    if (menuItem && 'id' in menuItem && menuItem?.id === 'pos_manager') {
      mutateGeneratePOSLink();
      trackMethod(SIDE_MENU_CLICK_EVENT, {
        platform: 'booking',
        location: 'side_menu_link',
        menu_item: 'pos_manager',
      });
      return;
    }

    if (e.key.includes('#/')) {
      window.location.hash = e.key;

      trackMethod(SIDE_MENU_CLICK_EVENT, {
        platform: 'booking',
        location: 'side_menu_link',
        menu_item: e.key.replace(/#\/|\//g, ''),
      });

      return;
    }

    if (e.key.startsWith('http://') || e.key.startsWith('https://')) {
      window.open(e.key, '_blank');
      trackMethod(SIDE_MENU_CLICK_EVENT, {
        platform: 'booking',
        location: 'side_menu_link',
        menu_item: e.key.replace(/#\/|\//g, ''),
      });
      return;
    }

    if (e.key.startsWith('/')) {
      const route = e.key;
      setSelectedKey(e.key);
      dispatch(changeLocation(route));
      trackMethod(SIDE_MENU_CLICK_EVENT, {
        platform: 'booking',
        location: 'side_menu_link',
        menu_item: e.key.replace(/#\/|\//g, ''),
      });
      eventBus.emit(EVENT_BUS_EVENTS.NAVIGATE, { payload: { route } });
    }
  };

  return (
    <StyledSider
      collapsible
      collapsed={sideMenuCollapsed}
      trigger={null}
      width={fullWidth ? '100%' : 260}
      collapsedWidth={80}
      $fullWidth={fullWidth}
    >
      {onClose ? (
        <StyledDrawerHeader align="center">
          <StyledDrawerCloseButton
            type="text"
            aria-label={t('side-menu.close-menu')}
            icon={<CloseOutlined />}
            onClick={onClose}
          />
          <SidebarLogo
            isLoading={isDataLoading}
            sideMenuCollapsed={sideMenuCollapsed}
            onNavigate={onNavigate}
          />
        </StyledDrawerHeader>
      ) : (
        <SidebarLogo
          isLoading={isDataLoading}
          sideMenuCollapsed={sideMenuCollapsed}
        />
      )}
      {
        // TODO: Make this loading state with
        // Skeleton Component for Menu
      }
      {isDataLoading ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'calc(100vh - 120px)',
            width: '100%',
            gap: 12,
          }}
        >
          <Spin size="large" />
          <div style={{ color: '#8c8c8c', fontSize: 14 }}>Loading...</div>
        </div>
      ) : (
        <>
          <StyledMenu
            mode="inline"
            selectedKeys={[selectedKey]}
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
            items={menuItems}
            onClick={handleOnMenuClick}
          />
          <SidebarActionCards sideMenuCollapsed={sideMenuCollapsed} />
        </>
      )}
    </StyledSider>
  );
}
